import { defineComponent, h, watch } from 'vue-demi';
import { useUrlIssueIdCheck } from 'liga-detail-en/src/hooks/useUrlIssueIdCheck';
import { useEventBusListener } from 'liga-detail-en/src/hooks/useEventListener';
import { useInstance } from 'common-module/src/util/useInstance';
import { useIssueDetail } from 'liga-detail-en/src/stores';
import { pick, isNumber, isArray, omit } from 'lodash';
import { RecorderState } from 'liga-detail-en/src/models/recorder';
import { getSystemUrlSource } from 'liga-detail-en/src/hooks/useUrlIssueIdCheck';

const ISSUE_EDIT = 'issue:edit';
const ISSUE_CREATE = 'issue.create';
const ISSUE_FORCE_CLOSE = 'issue:force:close';
const LAYOUT_SAVE = 'config-field-layout:save';
const PREVIEW_TYPE = 'issue:preview-issue-type';
const RECORD_STATE = 'recorder:state';
const RECORD_START = 'recorder:start';
const VIDEO_EXTRACT_UPDATE = 'video-extract:update';

const SOURCE_TRACK_MAP = {
  'next': '下一个',
  'prev': '上一个',
  'relation': '关联'
} as const;

type IssueDetailProps = {
  source?: string;
  options?: Record<string, any>;
}

export default defineComponent({
  name: 'IssueDetailWarper',
  components: {
    IssueWarper: () => import('./IssueDrawer.vue')
  },
  setup() {

    const instance = useInstance();
    const store = useIssueDetail();

    watch(() => store.loading, 
      (val) => {
        if (!val) {
          trackSimpleIssueVisit((store?.action as any)?.from);
        }
      }
    );

    watch(() => store.action,
      (action: any) => {
        if (action) {
          trackIssueVisit(action?.from);
        }
      }
    );
    
    function trackIssueVisit(from: string) {
      const source = SOURCE_TRACK_MAP[from as keyof typeof SOURCE_TRACK_MAP] || from;
      // 打开详情埋点
      instance.$track.send('Issue_Visit', { source, top_state: 'normal' }, true);
    }

    const trackSimpleIssueVisit = async (from: String | undefined) => {
      try {
        const templateCode = store.issueAssemble?.issueVO?.data?.templateCode;
        if (templateCode) {
          const source = SOURCE_TRACK_MAP[from as keyof typeof SOURCE_TRACK_MAP] || from;
          instance.$track.send('SampleIssue_Visit', { source, mark: templateCode }, true);
        }
      } catch (error) {
        console.log(error);
      }
    };

    const showIssueDetail = (issueDetailProps: IssueDetailProps) => {
      const { source, options = {} } = issueDetailProps;
      console.log('showIssueDetail', source, options);
      if (!options?.issueId) {
        store.createIssue(
          options.projectId,
          options.sprintId,
          options.issueTypeId,
          options.defaultData,
          options.copyFromIssueId
        );
        return;
      }

      store.editIssue(
        Number(options.issueId),
        options.turnPage,
        options.relationNavigationStack,
        options.force,
        options.commentId,
        options.scrollTop,
        options.isRead,
        source
      );
    };

    // 链接打开详情
    const { issueId, projectId, commentId, source, reminderId } = useUrlIssueIdCheck();
    if (issueId && projectId) {
      const props: IssueDetailProps = {
        source: source || '链接打开',
        options: {
          issueId,
          projectId,
          commentId,
          reminderId
        }
      };

      if (reminderId && isNumber(reminderId)) {
        store.awaitDataMarkCommentMessageRead(reminderId);
      }

      showIssueDetail(props);
    }

    // 监听强制关闭事件
    useEventBusListener(instance, ISSUE_FORCE_CLOSE, () => {
      store.close();
    });

    // 监听编辑工作事件打开详情
    useEventBusListener(instance, ISSUE_EDIT, (action: any) => {
      showIssueDetail({
        source: action.source || getSystemUrlSource() || '网站内部',
        options: {
          issueId: action.id,
          turnPage: pick(action, ['checkNextAndPrevious', 'nextIssue', 'previousIssue']),
          ...action
        }
      });
      if (action.activeTab) {
        setTimeout(
          () =>{
            store.changeRightTab(action.activeTab);
          }, 0
        );
      }
    });

    // 监听创建工作事件打开详情
    useEventBusListener(instance, 'tool:action', (action: any, data: any) => {
      if (action === ISSUE_CREATE) {
        if (store.isVisitor){
          return;
        }
        // 如果是看板页面 从看板获取默认值
        let issueTypeId = data?.issueTypeId;
        let projectId = data?.projectId;
        const currentProject = instance.$store.state.app.currentProject;
        if (!issueTypeId && !projectId && currentProject) {
          const kanbanSelectIssueTypes = (
              document.querySelector(
                '.zk-the-kanban-header .el-select[data-field-code="issue-type"]'
              ) as any
            )?.__vue__.value as number[];
          if (isArray(kanbanSelectIssueTypes) && kanbanSelectIssueTypes.length === 1) {
            issueTypeId = kanbanSelectIssueTypes[0];
            projectId = currentProject;
          }
        }
        store.createIssue(
          projectId,
          data?.sprint,
          issueTypeId,
          omit(data || {}, [
            'id',
            'projectId',
            'sprint',
            'issueTypeId'
          ]),
          data?.id
        );
      }
    });

    // 预览 IssueType
    useEventBusListener(instance, PREVIEW_TYPE, ({ projectId, issueTypeId }: any) => {
      store.previewIssueType(projectId, issueTypeId);
    });

    // 字段布局更新
    useEventBusListener(instance, LAYOUT_SAVE, () => {
      if (store.action) {
        store.reloadLayout();
        if (store.action.type === 'Create') {
          store.createIssue(
            store.currentProjectId,
            store.currentSprintId ?? undefined,
            store.currentIssueType?.id,
            omit(
              {
                ...store.action.defaultData,
                ...store.fieldValues
              },
              ['projectId', 'sprint', 'issueTypeId']
            )
          );
          return;
        }
        store.refreshIssueDetail(store.action, true);
      }
    });

    // 录屏状态更新
    useEventBusListener(instance, RECORD_STATE, (state: RecorderState) => {
      store.setRecorderState(state);
    });

    // 录屏开始
    useEventBusListener(instance, RECORD_START, (options?: {issueAdd: boolean, issueId: number}, from?: string) => {
      store.setRecordOptions({
        issueId: options?.issueId,
        from
      });
    });

    // 更新描述
    useEventBusListener(instance, VIDEO_EXTRACT_UPDATE, (data: { issueId?: number, videoCode: string, content: string}) => {
      if (data.issueId && data.issueId !== store.action?.issueId) {
        return;
      }
      const el = document.querySelector(`[data-extract-text-code="${data.videoCode}"`);
      if (el) {
        el.innerHTML = data.content;
      }
      const temp = document.createElement('div');
      temp.innerHTML = store.fieldValues.description;
      const videoEl = temp.querySelector(`div[data-video-code="${data.videoCode}"`);
      if (!videoEl) {
        return;
      }
      videoEl.innerHTML = `<p>${data.content}</p>`;
      store.setFieldValue('description', temp.innerHTML);
      temp.remove();
    });

    console.log('🚀🚀🚀🚀 issue detail init 🚀🚀🚀🚀', issueId, projectId, commentId, source, reminderId);

    return () => h('IssueWarper', { ref: 'warper' });
  }
});
